import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Tabs,
  Tab,
  Box,
  SelectChangeEvent,
  Button,
  Select,
  MenuItem,
} from "@mui/material";

import SearchBar from "../../components/SearchBar";
import TouristVisaApplications from "../../features/applications/touristVisaApplications";
import StudyVisaApplications from "../../features/applications/studyVisaApplications";
import WorkVisaApplications from "../../features/applications/workVisaApplications";
import BusinessVisaApplications from "../../features/applications/businessVisaApplications";

import {
  useApplicationListMutation,
  useExportApplicationsMutation,
  useLazyApplicationCountsQuery,
  useLazyDeleteApplicationQuery,
  useUpdateApplicationMutation,
} from "../../service/applications";

import { ApplicationList } from "../../types/User";
import { errorToast, successToast } from "../../helpers";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody, PaymentPlanType, Permission } from "../../types/General";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import { isValidInput } from "../../utils/validations";
import useAuth from "../../hooks/useAuth";
import {
  APPLICATION_STATUS,
  PAYMENT_STATUS,
  USER_ROLES,
} from "../../utils/enums";
import { useGetAllPaymentPlanMutation } from "../../service/paymentPlan";
import { ApplicationApprovalModal } from "../../Modals/applicationApprovalModal";

enum FILTER_TYPES {
  ALL = 1,
  PENDING = 2,
  APPROVED = 3,
  Rejected = 4,
  Closed = 5,
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <>{children}</>}
    </div>
  );
}

const ManageApplications = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [hidePermission, setHidePermission] = useState<Permission>();
  const [searchTerm, setSearchTerm] = useState("");
  const [mainTab, setMainTab] = useState(0);
  const [paymentPlansList, setPaymentPlansList] = useState<PaymentPlanType[]>(
    []
  );
  const [rows, setRows] = useState<ApplicationList[]>([]);

  const [applicationStatuses, setApplicationStatuses] = useState<{
    [key: number]: string;
  }>({});
  const [touristAppplicationCount, setTouristAppplicationCount] =
    useState<number>(0);
  const [studyApplicationCount, setStudyApplicationCount] = useState<number>(0);
  const [workApplicationCount, setWorkApplicationCount] = useState<number>(0);
  const [businessApplicationCount, setBusinessApplicationCount] =
    useState<number>(0);

  const [paymentPlan, setPaymentPlan] = useState<string>("default");
  const [rejectionReason, setRejectionReson] = useState("");

  const [openApprovalModal, setOpenApprovalModal] = useState(false);
  const [openCloseApplicationModal, setOpenCloseApplicationModal] =
    useState(false);

  const [applicationStatus, setApplicationStatus] = useState("default");
  const [selectedRow, setSelectedRow] = useState<ApplicationList | null>(null);

  const handleApplicationStatus =
    (row: ApplicationList) => async (event: SelectChangeEvent) => {
      const rowId = row?.id;
      const selectedStatus = event.target.value;
      if (
        Number(selectedStatus) ===
          APPLICATION_STATUS?.CONTRACT_AND_INITIAL_PAYMENT &&
        row?.payment_status !== PAYMENT_STATUS.FIRST_FULL_PAYMENT
      ) {
        errorToast(
          "The status cannot be changed as first payment isn't received yet "
        );
        return;
      }
      if (
        Number(selectedStatus) === APPLICATION_STATUS?.CHECKLIST_COMPLETION &&
        row?.payment_status !== PAYMENT_STATUS.SECOND_PAYMENT
      ) {
        errorToast(
          "The status cannot be changed as second payment isn't received yet "
        );
        return;
      }

      setApplicationStatuses((prevStatuses) => ({
        ...prevStatuses,
        [rowId]: event.target.value,
      }));
      try {
        const body = {
          application_status: selectedStatus,
        };

        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await updateApplication({
          body: encryptedBody,
          id: rowId,
        }).unwrap();
        setLoading(false);

        if (response?.status === 200) {
          successToast("Application status changed successfully");
          getApplicationsList();
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    };

  const handlePaymentPlan = (event: SelectChangeEvent) => {
    setPaymentPlan(event.target.value);
  };

  const handleApprovalStatus = (event: SelectChangeEvent) => {
    setApplicationStatus(event.target.value as string);
  };

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(FILTER_TYPES.ALL);

  const [applicationsList] = useApplicationListMutation();
  const [deleteApplication] = useLazyDeleteApplicationQuery();
  const [getPaymentPlans] = useGetAllPaymentPlanMutation();
  const [updateApplication] = useUpdateApplicationMutation();
  const [applicationCounts] = useLazyApplicationCountsQuery();
  const [exportCsv] = useExportApplicationsMutation();

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleDeleteApplication = async () => {
    setLoading(true);
    try {
      const response = await deleteApplication({
        id: selectedId,
      }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast("Deleted successfully");
        getApplicationsList();
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    setLoading(true);
    try {
      const response = await applicationCounts().unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setTouristAppplicationCount(response?.data?.VisitVisa || 0);
        setWorkApplicationCount(response?.data?.WorkVisa || 0);
        setBusinessApplicationCount(response?.data?.BusinessVisa || 0);
        setStudyApplicationCount(response?.data?.StudyVisa || 0);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const getApplicationsList = async () => {
    setLoading(true);
    try {
      let body;
      body = {
        start: page,
        length: 10,
        search: debouncedSearchTerm.trim(),
        visa_type: mainTab + 1,
      };
      if (statusFilter === FILTER_TYPES.APPROVED) {
        body = {
          ...body,
          is_verified: true,
          is_closed: false,
        };
      } else if (statusFilter === FILTER_TYPES.PENDING) {
        body = {
          ...body,
          is_verified: false,
          is_rejected: false,
          is_closed: false,
        };
      } else if (statusFilter === FILTER_TYPES.Rejected) {
        body = {
          ...body,
          is_rejected: true,
          is_closed: false,
        };
      } else if (statusFilter === FILTER_TYPES.Closed) {
        body = {
          ...body,
          is_closed: true,
        };
      }

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await applicationsList(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRows(response?.data?.response_object || []);
        setCount(response?.data?.total_records);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const getAllPaymentPlans = async () => {
    setLoading(true);
    try {
      const body = {
        start: page,
        length: 50,
        search: debouncedSearchTerm.trim(),
      } as any;

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getPaymentPlans(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setPaymentPlansList(response?.data?.response_object || []);
      } else {
        setPaymentPlansList([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleApproveReject = async () => {
    if (applicationStatus === "default") {
      errorToast("Please select approve or reject option");
      return;
    }
    if (applicationStatus === "APPROVE" && paymentPlan === "default") {
      errorToast("Please select a payment plan first");
      return;
    }
    if (applicationStatus !== "APPROVE" && rejectionReason === "") {
      errorToast("Please enter the rejection reason");
      return;
    }
    try {
      const body = {
        ...(applicationStatus === "APPROVE"
          ? { is_verified: true }
          : { is_rejected: true }),
        ...(applicationStatus === "APPROVE"
          ? { payment_plan: paymentPlan }
          : { reject_reason: rejectionReason }),
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await updateApplication({
        body: encryptedBody,
        id: selectedId,
      }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast(
          applicationStatus === "APPROVE"
            ? "Approved successfully"
            : "Rejected successfully"
        );
        setOpenApprovalModal(false);
        getApplicationsList();
        setPaymentPlan("default");
        setApplicationStatus("default");
        setRejectionReson("");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleCloseApplication = async () => {
    const body = {
      is_closed: true,
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await updateApplication({
        body: encryptedBody,
        id: selectedId,
      }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast("Closed successfully");
        setOpenCloseApplicationModal(false);
        getApplicationsList();
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      let body;
      body = {
        visa_type: mainTab + 1,
        search: searchTerm,
      };
      if (statusFilter === FILTER_TYPES.APPROVED) {
        body = {
          ...body,
          is_verified: true,
          is_closed: false,
        };
      } else if (statusFilter === FILTER_TYPES.PENDING) {
        body = {
          ...body,
          is_verified: false,
          is_rejected: false,
          is_closed: false,
        };
      } else if (statusFilter === FILTER_TYPES.Rejected) {
        body = {
          ...body,
          is_rejected: true,
          is_closed: false,
        };
      } else if (statusFilter === FILTER_TYPES.Closed) {
        body = {
          ...body,
          is_closed: true,
        };
      }

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await exportCsv(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        window.open(response?.file_urls);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllPaymentPlans();
  }, []);

  useEffect(() => {
    fetchCount();
    getApplicationsList();
  }, [debouncedSearchTerm, page, mainTab, statusFilter]);

  const handleFilter = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as any as FILTER_TYPES);
  };

  const checkPermission = () => {
    const permission = userData?.role_permission;
    if (permission?.length) {
      const foundPermission = permission?.find(
        (ele: Permission) => ele?.module === "Manage Applications"
      );

      if (foundPermission) {
        setHidePermission(foundPermission);
      } else {
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    if (userData?.user_role_type === USER_ROLES.SUBADMIN) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    if (typeof hidePermission !== "undefined") {
      if (!hidePermission?.can_view) {
        navigate("/permission-denied", { replace: true });
      }
    }
  }, [hidePermission, userData]);

  useEffect(() => {
    if (rows?.length) {
      const initialStatuses: { [key: number]: string } = {};
      rows.forEach((row) => {
        initialStatuses[row?.id] =
          row?.application_status?.toString() || "default"; // Ensure it's a string
      });
      setApplicationStatuses(initialStatuses);
    }
  }, [rows]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Applications</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
          Applications
        </p>
      </div>
      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              <Box>
                <Button
                  className="btn btn_highlight"
                  onClick={handleExportCSV}
                  disabled={!rows?.length}
                >
                  <img src="/static/images/upload_icon.svg" alt="Icon" /> Export
                  CSV
                </Button>
              </Box>
              <Select
                className="dashSelect"
                labelId="statusFilter"
                id="statusFilter"
                value={`${statusFilter}`}
                onChange={handleFilter}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={FILTER_TYPES.ALL}>All</MenuItem>
                <MenuItem value={FILTER_TYPES.APPROVED}>Approved</MenuItem>
                <MenuItem value={FILTER_TYPES.PENDING}>Pending</MenuItem>
                <MenuItem value={FILTER_TYPES.Rejected}>Rejected</MenuItem>
                <MenuItem value={FILTER_TYPES.Closed}>Closed</MenuItem>
              </Select>
            </div>
          </div>
          <Tabs
            value={mainTab}
            onChange={(_, newValue) => {
              setMainTab(newValue);
              setPage(1);
            }}
            className="custom_tabs_links"
          >
            <Tab label={`Visit Visa (${touristAppplicationCount})`} />
            <Tab label={`Study Visa (${studyApplicationCount})`} />
            <Tab label={`Work Visa (${workApplicationCount})`} />
            <Tab label={`Business Visa (${businessApplicationCount})`} />
          </Tabs>

          {/* Salaried Agents List */}
          <CustomTabPanel value={mainTab} index={0}>
            <TouristVisaApplications
              rows={rows}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              count={count}
              page={page}
              handlePageChange={handlePageChange}
              loading={loading}
              setOpen={setOpen}
              hidePermission={hidePermission}
              applicationStatuses={applicationStatuses}
              handleApplicationStatus={handleApplicationStatus}
              setOpenApprovalModal={setOpenApprovalModal}
              setSelectedRow={setSelectedRow}
              selectedRow={selectedRow}
              setOpenCloseApplicationModal={setOpenCloseApplicationModal}
            />
          </CustomTabPanel>
          <CustomTabPanel value={mainTab} index={1}>
            <StudyVisaApplications
              rows={rows}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              count={count}
              page={page}
              handlePageChange={handlePageChange}
              loading={loading}
              setOpen={setOpen}
              hidePermission={hidePermission}
              applicationStatuses={applicationStatuses}
              handleApplicationStatus={handleApplicationStatus}
              setOpenApprovalModal={setOpenApprovalModal}
              setSelectedRow={setSelectedRow}
              selectedRow={selectedRow}
              setOpenCloseApplicationModal={setOpenCloseApplicationModal}
            />
          </CustomTabPanel>
          <CustomTabPanel value={mainTab} index={2}>
            <WorkVisaApplications
              rows={rows}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              count={count}
              page={page}
              handlePageChange={handlePageChange}
              loading={loading}
              setOpen={setOpen}
              hidePermission={hidePermission}
              applicationStatuses={applicationStatuses}
              handleApplicationStatus={handleApplicationStatus}
              setOpenApprovalModal={setOpenApprovalModal}
              setSelectedRow={setSelectedRow}
              selectedRow={selectedRow}
              setOpenCloseApplicationModal={setOpenCloseApplicationModal}
            />
          </CustomTabPanel>
          <CustomTabPanel value={mainTab} index={3}>
            <BusinessVisaApplications
              rows={rows}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              count={count}
              page={page}
              handlePageChange={handlePageChange}
              loading={loading}
              setOpen={setOpen}
              hidePermission={hidePermission}
              applicationStatuses={applicationStatuses}
              handleApplicationStatus={handleApplicationStatus}
              setOpenApprovalModal={setOpenApprovalModal}
              setSelectedRow={setSelectedRow}
              selectedRow={selectedRow}
              setOpenCloseApplicationModal={setOpenCloseApplicationModal}
            />
          </CustomTabPanel>
        </Box>
        <ConfirmationModal
          text={"Are you sure to delete this application?"}
          openModal={open}
          setOpenModal={setOpen}
          handleSubmit={handleDeleteApplication}
        />
        <ConfirmationModal
          text={"Are you sure to close this application?"}
          openModal={openCloseApplicationModal}
          setOpenModal={setOpenCloseApplicationModal}
          handleSubmit={handleCloseApplication}
        />
        <ApplicationApprovalModal
          openModal={openApprovalModal}
          setOpenModal={setOpenApprovalModal}
          handleSubmit={handleApproveReject}
          applicationStatus={applicationStatus}
          handleApprovalStatus={handleApprovalStatus}
          paymentPlan={paymentPlan}
          handlePaymentPlan={handlePaymentPlan}
          paymentPlansList={paymentPlansList}
          setApplicationStatus={setApplicationStatus}
          setPaymentPlan={setPaymentPlan}
          setRejectionReson={setRejectionReson}
          rejectionReason={rejectionReason}
        />
      </div>
    </MainContainer>
  );
};

export default ManageApplications;
